import React, { useMemo } from 'react';
import { Modal, Form, Input, Select, Typography, message, Spin } from 'antd';
import { ShopOutlined } from '@ant-design/icons';
import { useAuth } from '../../hooks/useAuth';
import {
    useCreateAgencyClientOrgMutation,
    useGetAgencyBusinessTypesQuery,
    useGetAgencyProductTypesQuery,
    useGetQuestionTemplateTypesQuery,
    IQuestionTemplateType,
} from '../../services/nestjs-base-api';
import { FormattedMessage, useIntl } from 'react-intl';

const { Text } = Typography;

interface CreateClientOrgModalProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Resolve a templateType from the selected benchmark business/product type
 * slugs using the config-driven registry (no hardcoded map). Exact bt+pt match
 * first; else the first active row for the business type (when product type
 * wasn't chosen / doesn't disambiguate); else undefined.
 */
function resolveTemplateType(
    registry: IQuestionTemplateType[],
    businessTypeSlug?: string,
    productTypeSlug?: string,
): string | undefined {
    if (!businessTypeSlug) return undefined;
    const exact = registry.find(
        (r) =>
            r.benchmarkBusinessTypeSlug === businessTypeSlug &&
            r.benchmarkProductTypeSlug === productTypeSlug,
    );
    if (exact) return exact.slug;
    const byBusinessType = registry
        .filter((r) => r.benchmarkBusinessTypeSlug === businessTypeSlug)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    return byBusinessType[0]?.slug;
}

export const CreateClientOrgModal: React.FC<CreateClientOrgModalProps> = ({ open, onClose }) => {
    const [form] = Form.useForm();
    const { user } = useAuth();
    const intl = useIntl();
    const [createClientOrg, { isLoading }] = useCreateAgencyClientOrgMutation();

    const { data: businessTypes, isLoading: btLoading } = useGetAgencyBusinessTypesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !open },
    );

    const { data: productTypes, isLoading: ptLoading } = useGetAgencyProductTypesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !open },
    );

    const { data: templateTypeRegistry } = useGetQuestionTemplateTypesQuery(
        { jwtToken: user?.jwtToken || '' },
        { skip: !open },
    );

    const selectedBusinessTypeId = Form.useWatch('businessTypeId', form);

    // Filter product types based on the selected business type's valid associations
    const filteredProductTypes = useMemo(() => {
        if (!productTypes || !selectedBusinessTypeId || !businessTypes) return [];
        const bt = businessTypes.find((b: any) => b.id === selectedBusinessTypeId);
        if (!bt?.productTypes) return productTypes;
        const validIds = bt.productTypes.map((pt: any) => pt.id);
        return productTypes.filter((pt: any) => validIds.includes(pt.id));
    }, [productTypes, selectedBusinessTypeId, businessTypes]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            // Derive templateType from business type + product type selection
            const selectedBt = businessTypes?.find((b: any) => b.id === values.businessTypeId);
            const selectedPt = productTypes?.find((p: any) => p.id === values.benchmarkProductTypeId);
            const templateType = resolveTemplateType(
                templateTypeRegistry || [],
                selectedBt?.slug,
                selectedPt?.slug,
            );

            if (!templateType) {
                message.error(
                    intl.formatMessage({
                        id: 'agency.createClient.noTemplateType',
                        defaultMessage:
                            'No template type is configured for the selected business/product type. Add a mapping under Statement Templates → Type Wiring.',
                    }),
                );
                return;
            }

            await createClientOrg({
                jwtToken: user?.jwtToken || '',
                payload: {
                    organisationName: values.organisationName,
                    brandName: values.brandName || undefined,
                    templateType,
                    businessTypeId: values.businessTypeId,
                    benchmarkProductTypeId: values.benchmarkProductTypeId || undefined,
                    websiteUrl: values.websiteUrl || undefined,
                    notes: values.notes || undefined,
                },
            }).unwrap();

            message.success(
                intl.formatMessage({
                    id: 'agency.createClient.success',
                    defaultMessage: 'Client organisation created successfully',
                })
            );
            form.resetFields();
            onClose();
        } catch (error: any) {
            message.error(error?.data?.message || 'Failed to create client organisation');
        }
    };

    return (
        <Modal
            title={
                <>
                    <ShopOutlined style={{ marginRight: 8 }} />
                    <FormattedMessage id="agency.createClient.title" defaultMessage="Create Client Organisation" />
                </>
            }
            open={open}
            onOk={handleSubmit}
            onCancel={() => { form.resetFields(); onClose(); }}
            confirmLoading={isLoading}
            okText={intl.formatMessage({ id: 'agency.createClient.create', defaultMessage: 'Create Client' })}
            width={600}
        >
            {(btLoading || ptLoading) ? (
                <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>
            ) : (
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="organisationName"
                        label={<FormattedMessage id="agency.createClient.orgName" defaultMessage="Organisation Name" />}
                        rules={[{ required: true, message: 'Organisation name is required' }]}
                    >
                        <Input placeholder="e.g. Acme Corp" />
                    </Form.Item>

                    <Form.Item
                        name="brandName"
                        label={<FormattedMessage id="agency.createClient.brandName" defaultMessage="Brand Name" />}
                        extra={<FormattedMessage id="agency.createClient.brandNameHint" defaultMessage="Leave blank to use organisation name" />}
                    >
                        <Input placeholder="e.g. Acme" />
                    </Form.Item>

                    <Form.Item
                        name="businessTypeId"
                        label={<FormattedMessage id="agency.createClient.businessType" defaultMessage="Business Type" />}
                        rules={[{ required: true, message: 'Business type is required' }]}
                    >
                        <Select
                            placeholder="Select business type"
                            options={businessTypes?.map((bt: any) => ({
                                value: bt.id,
                                label: bt.name,
                            })) || []}
                            onChange={() => form.setFieldValue('benchmarkProductTypeId', undefined)}
                        />
                    </Form.Item>

                    {filteredProductTypes.length > 0 && (
                        <Form.Item
                            name="benchmarkProductTypeId"
                            label={<FormattedMessage id="agency.createClient.benchmarkProductType" defaultMessage="Product Type" />}
                        >
                            <Select
                                placeholder="Select product type"
                                allowClear
                                options={filteredProductTypes.map((pt: any) => ({
                                    value: pt.id,
                                    label: pt.name,
                                }))}
                            />
                        </Form.Item>
                    )}

                    <Form.Item
                        name="websiteUrl"
                        label={<FormattedMessage id="agency.createClient.website" defaultMessage="Website URL" />}
                    >
                        <Input placeholder="https://example.com" />
                    </Form.Item>

                    <Form.Item
                        name="notes"
                        label={<FormattedMessage id="agency.createClient.notes" defaultMessage="Notes" />}
                    >
                        <Input.TextArea rows={2} placeholder="Internal notes about this client" />
                    </Form.Item>

                </Form>
            )}
        </Modal>
    );
};
